// web/src/services/favoritesStore.ts
// Persists favorited photos to IndexedDB with image data for offline access.
// Stores the original image blob so users can re-download without scores/watermarks.

const DB_NAME = 'frame_favorites';
const DB_VERSION = 1;
const STORE_NAME = 'photos';

export interface FavoritePhoto {
  id: string;
  compositeScore: number;
  dimensions: Record<string, number>;
  mode: string;
  favoritedAt: string;
  thumbnailBlob?: Blob;
  originalBlob?: Blob;
  fileName?: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export const FavoritesStore = {
  async getAll(): Promise<FavoritePhoto[]> {
    try {
      const db = await openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result ?? []);
        req.onerror = () => resolve([]);
      });
    } catch { return []; }
  },

  async get(id: string): Promise<FavoritePhoto | null> {
    try {
      const db = await openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(id);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => resolve(null);
      });
    } catch { return null; }
  },

  async add(photo: FavoritePhoto): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(photo);
    } catch { /* IDB unavailable */ }
  },

  async remove(id: string): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
    } catch { /* IDB unavailable */ }
  },

  async has(id: string): Promise<boolean> {
    const item = await this.get(id);
    return item !== null;
  },

  async getIds(): Promise<Set<string>> {
    const all = await this.getAll();
    return new Set(all.map(p => p.id));
  },

  async clear(): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
    } catch { /* IDB unavailable */ }
  },
};
